import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AppVersionBanner from "@/components/AppVersionBanner";
import ProtectedRoute from "@/components/ProtectedRoute";

// Only Home is eager — every other page is code-split so the initial JS
// bundle is tiny. Public visitors never download the ~5000-line admin bundle
// unless they actually navigate to /admin/*. Fallback = a skeleton line so
// the layout doesn't jump when a chunk streams in.
import Home from "@/pages/Home";

const Auctions              = lazy(() => import("@/pages/Auctions"));
const AuctionDetail         = lazy(() => import("@/pages/AuctionDetail"));
const LotDetail             = lazy(() => import("@/pages/LotDetail"));
const Login                 = lazy(() => import("@/pages/Login"));
const Register              = lazy(() => import("@/pages/Register"));
const Dashboard             = lazy(() => import("@/pages/Dashboard"));
const Consign               = lazy(() => import("@/pages/Consign"));
const About                 = lazy(() => import("@/pages/About"));
const Rules                 = lazy(() => import("@/pages/Rules"));
const Terms                 = lazy(() => import("@/pages/Terms"));
const ForgotPassword        = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword         = lazy(() => import("@/pages/ResetPassword"));
const AdminLayout           = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard        = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminAuctions         = lazy(() => import("@/pages/admin/AdminAuctions"));
const AdminLots             = lazy(() => import("@/pages/admin/AdminLots"));
const AdminLotQuickEdit     = lazy(() => import("@/pages/admin/AdminLotQuickEdit"));
const AdminUsers            = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminUserData         = lazy(() => import("@/pages/admin/AdminUserData"));
const AdminConsignments     = lazy(() => import("@/pages/admin/AdminConsignments"));
const AdminLotBids          = lazy(() => import("@/pages/admin/AdminLotBids"));
const AdminAuctionDetail    = lazy(() => import("@/pages/admin/AdminAuctionDetail"));
const AdminConsignorStatement = lazy(() => import("@/pages/admin/AdminConsignorStatement"));
const PaymentSuccess        = lazy(() => import("@/pages/PaymentSuccess"));
const Marketplace           = lazy(() => import("@/pages/Marketplace"));
const MarketplaceListingDetail = lazy(() => import("@/pages/MarketplaceListingDetail"));
const SellInMarketplace     = lazy(() => import("@/pages/SellInMarketplace"));
const MarketplaceAgreement  = lazy(() => import("@/pages/MarketplaceAgreement"));
const AdminMarketplace      = lazy(() => import("@/pages/admin/AdminMarketplace"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function MetaPixelTracker() {
  // SPA route changes don't trigger a full page reload, so the Meta Pixel only
  // fires on the initial load. This component fires `PageView` again on every
  // client-side navigation so Facebook Ads attribution stays accurate.
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof window.fbq !== "function") return;
    window.fbq("track", "PageView");
  }, [pathname, search]);
  return null;
}

function Shell({ children, hideChrome = false }) {
  return (
    <div className="App min-h-screen flex flex-col">
      <AppVersionBanner />
      {!hideChrome && <Navbar />}
      <main className="flex-1">{children}</main>
      {!hideChrome && <Footer />}
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <MetaPixelTracker />
        {/* Minimal fallback while a route chunk is loading. Height reserved so
            the layout doesn't jump; no spinner because most chunks stream in
            in under 200ms on the second visit thanks to browser caching. */}
        <Suspense fallback={<div className="min-h-[50vh]" aria-busy="true" data-testid="route-loading" />}>
          <Routes>
          <Route path="/" element={<Shell><Home /></Shell>} />
          <Route path="/auctions" element={<Shell><Auctions /></Shell>} />
          <Route path="/auctions/:id" element={<Shell><AuctionDetail /></Shell>} />
          <Route path="/lots/:id" element={<Shell><LotDetail /></Shell>} />
          <Route path="/login" element={<Shell><Login /></Shell>} />
          <Route path="/register" element={<Shell><Register /></Shell>} />
          <Route path="/forgot-password" element={<Shell><ForgotPassword /></Shell>} />
          <Route path="/reset-password" element={<Shell><ResetPassword /></Shell>} />
          <Route path="/about" element={<Shell><About /></Shell>} />
          <Route path="/rules" element={<Shell><Rules /></Shell>} />
          <Route path="/terms" element={<Shell><Terms /></Shell>} />
          <Route path="/consign" element={<Shell><Consign /></Shell>} />
          <Route path="/marketplace" element={<Shell><Marketplace /></Shell>} />
          <Route path="/marketplace/:id" element={<Shell><MarketplaceListingDetail /></Shell>} />
          <Route path="/sell" element={<ProtectedRoute><Shell><SellInMarketplace /></Shell></ProtectedRoute>} />
          <Route path="/sell/:id/edit" element={<ProtectedRoute><Shell><SellInMarketplace /></Shell></ProtectedRoute>} />
          <Route path="/marketplace-agreement" element={<Shell><MarketplaceAgreement /></Shell>} />
          <Route path="/payment-success" element={<Shell><PaymentSuccess /></Shell>} />
          <Route path="/dashboard" element={<ProtectedRoute><Shell><Dashboard /></Shell></ProtectedRoute>} />

          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="auctions" element={<AdminAuctions />} />
            <Route path="auctions/:auctionId" element={<AdminAuctionDetail />} />
            <Route path="lots" element={<AdminLots />} />
            <Route path="lots/quick-edit" element={<AdminLotQuickEdit />} />
            <Route path="lots/:lotId/bids" element={<AdminLotBids />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="user-data" element={<AdminUserData />} />
            <Route path="consignments" element={<AdminConsignments />} />
            <Route path="marketplace" element={<AdminMarketplace />} />
            <Route path="consignors/:userId/statement" element={<AdminConsignorStatement />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
